// Register route — '/register?token=…'.
//
// Public route (NOT under /admin). The /admin/* layout requires an admin
// session; an invitee has neither token nor account yet, so this lives at
// the top level. The invite URL minted by POST /api/admin/invite points
// here (see www/livevane.com/api/admin/index.php).
//
// Flow:
//   1. Read ?token=… from the URL.
//   2. GET /api/admin/invite/{token} to preflight — surfaces the email
//      pre-bound to the invite plus the expiry. 404 → invite is invalid
//      or expired; render a dead-end card so the recipient has a clear
//      next step (ask the admin for a fresh link).
//   3. POST /api/admin/register {token, name, password}. On success the
//      PHP layer issues a token pair so we sign the new admin in
//      immediately and bounce them to /admin.
import { useState } from 'react'
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { apiClient, ApiError } from '@/lib/api-client'
import { resolveAccessExpiry, useAuthStore } from '@/stores/auth-store'
import type {
  AdminInvitePreflightResponse,
  AdminRegisterResponse,
  AuthUser,
} from '@/lib/types'

const registerSchema = z.object({
  name: z.string().min(1, 'Name is required.'),
  password: z.string().min(8, 'Password must be at least 8 characters.'),
})

type RegisterFormValues = z.infer<typeof registerSchema>

type RegisterSearch = { token?: string }

export const Route = createFileRoute('/register')({
  validateSearch: (s: Record<string, unknown>): RegisterSearch => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: RegisterPage,
})

function RegisterPage() {
  useDocumentTitle('Create your account')
  const navigate = useNavigate()
  const { token } = useSearch({ from: '/register' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Preflight — surfaces the invitee's email and validates the token
  // before we render the form. enabled=false when token is missing so we
  // don't issue a doomed request.
  const preflight = useQuery({
    queryKey: ['register', 'preflight', token ?? ''] as const,
    queryFn: () =>
      apiClient.get<AdminInvitePreflightResponse>(
        `/admin/invite/${encodeURIComponent(token ?? '')}`,
        { skipAuthRedirect: true },
      ),
    enabled: typeof token === 'string' && token !== '',
    retry: false,
  })

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', password: '' },
  })

  const onSubmit = async (values: RegisterFormValues): Promise<void> => {
    if (typeof token !== 'string' || token === '') return
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiClient.post<AdminRegisterResponse>(
        '/admin/register',
        { token, name: values.name, password: values.password },
        { skipAuthRedirect: true },
      )

      if (
        !res ||
        res.success !== true ||
        !res.access_token ||
        !res.refresh_token
      ) {
        throw new ApiError('Registration failed.', 400)
      }

      // Backend (JWTAuth::login via /api/admin/register) returns the
      // user object; fall back to invite preflight email when an older
      // backend deploy hasn't shipped that change yet.
      const user: AuthUser = res.user ?? {
        id: 'me',
        email: preflight.data?.email ?? '',
        role: 'admin',
      }
      useAuthStore.getState().setSession(
        res.access_token,
        user,
        res.refresh_token,
        resolveAccessExpiry(res.expires_in),
      )
      toast.success('Account created. Signing you in…')
      await navigate({ to: '/admin', replace: true })
    } catch (err) {
      const message =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Registration failed.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  // ── Token missing ────────────────────────────────────────────────
  if (typeof token !== 'string' || token === '') {
    return (
      <RegisterShell title="Invite required">
        <p className="text-sm text-muted-foreground">
          This page accepts an invite link. Ask the admin who invited you
          to resend their link, or{' '}
          <Link to="/login" className="text-cyber-cyan underline-offset-4 hover:underline">
            sign in
          </Link>{' '}
          if you already have an account.
        </p>
      </RegisterShell>
    )
  }

  // ── Preflight loading ────────────────────────────────────────────
  if (preflight.isLoading) {
    return (
      <RegisterShell title="Checking your invite…">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </RegisterShell>
    )
  }

  // ── Preflight error (invalid/expired token) ──────────────────────
  if (preflight.isError || preflight.data?.success !== true) {
    return (
      <RegisterShell title="Invite link not valid">
        <p className="text-sm text-muted-foreground">
          This invite link is invalid or has expired. Ask the admin who
          invited you to resend a fresh link.
        </p>
      </RegisterShell>
    )
  }

  // ── Happy path ───────────────────────────────────────────────────
  return (
    <RegisterShell
      title="Create your account"
      description="Pick a display name and a password to finish setting up your admin account."
    >
      {error ? (
        <div
          role="alert"
          data-testid="register-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-foreground"
        >
          {error}
        </div>
      ) : null}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="register-email">Email</Label>
            <Input
              id="register-email"
              type="email"
              name="email"
              autoComplete="username"
              readOnly
              value={preflight.data.email}
              aria-describedby="register-email-hint"
            />
            <p id="register-email-hint" className="text-[13px] text-muted-foreground">
              Set by your invite. You will sign in with this address.
            </p>
          </div>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display name</FormLabel>
                <FormControl>
                  <Input autoComplete="name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="text-[13px]">
                  At least 8 characters.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={submitting}
            data-testid="register-submit"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </Button>
          <p className="text-[13px] text-muted-foreground">
            You will be signed in and taken straight to the admin console.
          </p>
        </form>
      </Form>
    </RegisterShell>
  )
}

function RegisterShell({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <main className="access-art flex min-h-[calc(100dvh-3.5rem)] w-full flex-col items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 pb-2">
          <h1 className="font-display text-4xl leading-tight tracking-wide">{title}</h1>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
    </main>
  )
}
